import type * as z from 'zod';
import type { BusinessListFilter } from '../model/list-params';
import { presignSlotSchema, type PresignSlot } from '../model/media';
import {
  BUSINESS_FETCH_ALL_MAX_PAGES,
  BUSINESS_FETCH_ALL_PAGE_SIZE,
} from '../config/media';

export interface BusinessListResult<T> {
  data: T[];
  meta: { page: number; pageSize: number; totalPages: number; total: number };
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

/** The item array of a list envelope (`list`, `items`, `results`, or under `data`). */
export function listItems(raw: unknown): unknown[] {
  return firstArray(asRecord(raw));
}

function firstArray(root: Record<string, unknown>): unknown[] {
  const nested = asRecord(root.data);
  const candidates = [
    root.list,
    root.items,
    root.results,
    root.data,
    nested.list,
    nested.cuisines,
    root.cuisines,
    nested.results,
  ];
  const found = candidates.find((value) => Array.isArray(value));
  return Array.isArray(found) ? found : [];
}

export function normalizeBusinessList<T>(
  raw: unknown,
  itemSchema: z.ZodType<T>,
  filter: BusinessListFilter,
): BusinessListResult<T> {
  const root = asRecord(raw);
  const rows: T[] = [];
  for (const item of firstArray(root)) {
    const parsed = itemSchema.safeParse(item);
    if (parsed.success) rows.push(parsed.data);
  }
  const meta = asRecord(root.meta ?? root.pagination);
  const total = Number(meta.total ?? meta.total_count ?? meta.count) || rows.length;
  const totalPages =
    Number(meta.total_pages ?? meta.page_count ?? meta.last_page) ||
    (filter.limit > 0 ? Math.ceil(total / filter.limit) : 0);
  return {
    data: rows,
    meta: {
      page: filter.limit > 0 ? Math.floor(filter.offset / filter.limit) + 1 : 1,
      pageSize: filter.limit,
      totalPages,
      total,
    },
  };
}

export function unwrapDataPayload(raw: unknown): unknown {
  const root = asRecord(raw);
  return root.data ?? raw;
}

export function normalizePresignSlots(raw: unknown): PresignSlot[] {
  const slots: PresignSlot[] = [];
  for (const item of firstArray(asRecord(raw))) {
    const parsed = presignSlotSchema.safeParse(item);
    if (parsed.success) slots.push(parsed.data);
  }
  return slots;
}

/**
 * Walks every page of a list endpoint. These admin lists have no server-side
 * search/status filters, so a filtered view has to see the whole collection
 * rather than just the current page.
 */
export async function fetchAllBusinessPages<T>(
  fetchPage: (filter: BusinessListFilter) => Promise<BusinessListResult<T>>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 0; page < BUSINESS_FETCH_ALL_MAX_PAGES; page += 1) {
    const result = await fetchPage({
      limit: BUSINESS_FETCH_ALL_PAGE_SIZE,
      offset: page * BUSINESS_FETCH_ALL_PAGE_SIZE,
    });
    rows.push(...result.data);
    if (result.data.length === 0 || page + 1 >= result.meta.totalPages) break;
  }
  return rows;
}
